import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type { SyncFailure, SyncStatus } from "../../domain/entities/sharepoint"
import type { SyncJob } from "../hooks/use-sharepoint-sync"

type Props = {
  jobs: SyncJob[]
  onRetry: (job: SyncJob) => void
  onDismiss: (id: string) => void
}

const pctOf = (s: SyncStatus | null) =>
  s && s.total > 0 ? Math.round((s.done / s.total) * 100) : 0

const isRunning = (s: SyncStatus | null) =>
  !s || s.status === "preparing" || s.status === "running"

const Bar = ({ pct, done }: { pct: number; done: boolean }) => (
  <div
    className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={pct}
  >
    <div
      className={cn(
        "h-full rounded-full transition-all duration-300",
        done ? "bg-[#2f8f36]" : "bg-primary",
      )}
      style={{ width: `${pct}%` }}
    />
  </div>
)

const FailureList = ({ failed }: { failed: SyncFailure[] }) =>
  failed.length > 0 ? (
    <ul className="mt-1 space-y-0.5 text-xs text-red-500">
      {failed.map((f) => (
        <li key={`${f.itemId}:${f.name}`} className="truncate" title={f.name}>
          {f.name}: {f.error}
          {f.folder ? <span className="text-red-400"> (trong {f.folder})</span> : null}
        </li>
      ))}
    </ul>
  ) : null

const SyncProgressRow = ({
  job,
  onRetry,
  onDismiss,
}: {
  job: SyncJob
  onRetry: (job: SyncJob) => void
  onDismiss: (id: string) => void
}) => {
  const root = job.status
  const rootRunning = !job.errored && isRunning(root)
  const rootDone = !!root && !isRunning(root)

  // The latest retry attempt (if any) drives the live indicator on this row.
  const retryStatus = job.retry?.status ?? null
  const retryErrored = job.retry?.errored ?? false
  const retryRunning = job.retrying || (!!job.retry && !retryErrored && isRunning(retryStatus))
  const busy = rootRunning || retryRunning

  // Current failures = original permanent (4xx) failures + latest attempt's.
  const permanentFailed = (root?.failed ?? []).filter((f) => !f.retryable)
  const currentFailed = job.retry
    ? [...permanentFailed, ...(retryStatus?.failed ?? [])]
    : (root?.failed ?? [])

  // A new retry resends the retryable failures of the tail attempt.
  const tailStatus = job.retry ? retryStatus : root
  const retryableCount = (tailStatus?.failed ?? []).filter((f) => f.retryable).length
  const canRetry = !busy && retryableCount > 0

  const rootLabel = job.errored
    ? "Không khả dụng"
    : rootRunning
      ? `${root?.done ?? 0}/${root?.total ?? 0} file`
      : `Hoàn tất — ${root?.done ?? 0}/${root?.total ?? 0} file`

  return (
    <div className="px-3 py-2">
      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
        <span
          className={cn(
            "truncate",
            job.errored ? "text-red-500" : rootDone ? "text-[#2f8f36]" : "text-muted-foreground",
          )}
        >
          {rootLabel}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {canRetry ? (
            <Button
              variant="outline"
              size="sm"
              className="h-6 cursor-pointer px-2 text-xs"
              onClick={() => onRetry(job)}
            >
              Retry failed
            </Button>
          ) : null}
          {!busy ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Dismiss sync"
                  className="cursor-pointer rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={() => onDismiss(job.id)}
                >
                  <X className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Dismiss</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </div>

      {job.errored ? (
        <p className="text-xs text-red-500">Phiên có thể đã hết hạn — hãy đồng bộ lại.</p>
      ) : (
        <Bar pct={rootRunning ? pctOf(root) : root ? pctOf(root) : 0} done={rootDone} />
      )}

      {retryRunning || job.retry ? (
        <div className="mt-1.5">
          <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            {retryRunning ? <Spinner className="size-3" /> : null}
            <span className={cn(!retryRunning && !retryErrored && "text-[#2f8f36]")}>
              {retryRunning
                ? "Đang thử lại…"
                : retryErrored
                  ? "Thử lại: không khả dụng"
                  : `Thử lại: hoàn tất — ${retryStatus?.done ?? 0}/${retryStatus?.total ?? 0}`}
            </span>
          </div>
          {!job.retrying && !retryErrored && retryStatus ? (
            <Bar pct={pctOf(retryStatus)} done={!isRunning(retryStatus)} />
          ) : null}
        </div>
      ) : null}

      <FailureList failed={currentFailed} />
    </div>
  )
}

export const SyncProgressSection = ({ jobs, onRetry, onDismiss }: Props) => (
  <div className="rounded border">
    <div className="border-b px-3 py-2 text-sm font-medium">
      Tiến trình đồng bộ ({jobs.length})
    </div>
    <div className="max-h-[16vh] divide-y overflow-y-auto">
      {jobs.map((job) => (
        <SyncProgressRow key={job.id} job={job} onRetry={onRetry} onDismiss={onDismiss} />
      ))}
    </div>
  </div>
)
